import { useEffect, useState, useRef, useCallback } from 'react';
import { apiGet } from '../utils/api';
import { useDisguise } from '../context/DisguiseContext';

// 증시뉴스 피드 (네이버 경제·증권 섹션). 제목 클릭 → 새 탭.
// - 상단(1페이지)은 60초 자동갱신 (단, 스크롤 내려 추가로딩 중이면 일시정지)
// - 하단 도달 시 커서로 다음 페이지 append (무한스크롤, 최대 MAX_ITEMS)
const MAX_ITEMS = 200;          // 누적 상한 (약 6페이지)
const REFRESH_MS = 60 * 1000;

export default function MarketNews() {
  const { disguised, excel } = useDisguise();
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(false);   // 1페이지(초기/갱신) 로딩
  const [moreLoading, setMore]  = useState(false);   // 추가페이지 로딩
  const [cursor, setCursor]     = useState('');      // 다음 요청 커서
  const [hasNext, setHasNext]   = useState(true);
  const [expanded, setExpanded] = useState(false);   // 사용자가 스크롤로 확장했는지

  const listRef      = useRef(null);
  const sentinelRef  = useRef(null);
  const inFlight     = useRef(false);                // 중복요청 차단
  const seenUrls     = useRef(new Set());            // 중복기사 제거

  // ── 1페이지 로드 (초기 + 60초 갱신). 확장 상태면 갱신 스킵. ──
  const loadFirst = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const d = await apiGet('/api/market-news?cursor=');
      const rows = Array.isArray(d?.items) ? d.items : [];
      seenUrls.current = new Set(rows.map(r => r.url));
      setItems(rows);
      setCursor(d?.next_cursor || '');
      setHasNext(d?.has_next !== false);
    } catch (e) {
      console.error('증시뉴스 오류:', e);
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, []);

  // ── 다음 페이지 append (무한스크롤) ──
  const loadMore = useCallback(async () => {
    if (inFlight.current || !hasNext || !cursor) return;
    if (items.length >= MAX_ITEMS) return;
    inFlight.current = true;
    setMore(true);
    setExpanded(true);            // 확장됨 → 자동갱신 정지
    try {
      const d = await apiGet(`/api/market-news?cursor=${encodeURIComponent(cursor)}`);
      const rows = Array.isArray(d?.items) ? d.items : [];
      const fresh = rows.filter(r => !seenUrls.current.has(r.url));
      fresh.forEach(r => seenUrls.current.add(r.url));
      setItems(prev => [...prev, ...fresh].slice(0, MAX_ITEMS));
      setCursor(d?.next_cursor || '');
      setHasNext(d?.has_next !== false);
    } catch (e) {
      console.error('증시뉴스 추가로딩 오류:', e);
    } finally {
      setMore(false);
      inFlight.current = false;
    }
  }, [cursor, hasNext, items.length]);

  // 초기 로드 + 자동갱신 (확장 상태에선 갱신 안 함)
  useEffect(() => {
    loadFirst();
    const t = setInterval(() => {
      if (!expanded) loadFirst();
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, [loadFirst, expanded]);

  // IntersectionObserver로 하단 감지
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const reachedMax = items.length >= MAX_ITEMS;
    if (reachedMax || !hasNext) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { root: listRef.current, rootMargin: '120px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore, items.length, hasNext]);

  // 맨 위로 스크롤하면 확장 해제 → 자동갱신 재개
  const onScroll = () => {
    const el = listRef.current;
    if (el && el.scrollTop < 40 && expanded) setExpanded(false);
  };

  const borderC = excel ? '#d0d0d0' : '#21262d';
  const label   = excel ? '📄 시장 동향' : disguised ? '📰 Market Feed' : '증시뉴스';
  const reachedMax = items.length >= MAX_ITEMS;

  return (
    <div style={styles.panel}>
      <div style={{ ...styles.bar, borderBottom: `1px solid ${borderC}` }}>
        <span style={{ ...styles.label, color: excel ? '#000' : '#e6edf3', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {(!excel && !disguised) && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d29922" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M4 11 a7 7 0 0 1 7 7" />
              <path d="M4 5 a13 13 0 0 1 13 13" />
              <circle cx="5" cy="18" r="1.4" fill="#d29922" stroke="none" />
            </svg>
          )}
          {label}
        </span>
        {loading && <span style={styles.loading}>{disguised ? 'Loading...' : '로딩 중...'}</span>}
      </div>
      <div style={styles.list} ref={listRef} onScroll={onScroll}>
        {items.length === 0 && !loading && (
          <div style={styles.empty}>{disguised ? 'No items' : '뉴스 없음'}</div>
        )}
        {items.map((it, i) => (
          <a key={it.url || i} href={it.url} target="_blank" rel="noreferrer" style={styles.row}>
            <div style={{ ...styles.title, color: excel ? '#000' : '#c9d1d9' }}>{it.title}</div>
            {(it.press || it.time) && (
              <div style={styles.meta}>{[it.press, it.time].filter(Boolean).join(' · ')}</div>
            )}
          </a>
        ))}

        {/* 무한스크롤 sentinel + 상태 표시 */}
        {items.length > 0 && (
          <div ref={sentinelRef} style={styles.sentinel}>
            {moreLoading
              ? (disguised ? 'Loading more...' : '불러오는 중...')
              : reachedMax
                ? (disguised ? 'End of feed' : '최신 뉴스를 모두 불러왔습니다')
                : !hasNext
                  ? (disguised ? 'End of feed' : '더 이상 뉴스가 없습니다')
                  : ''}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  panel:    { height: '100%', display: 'flex', flexDirection: 'column' },
  bar:      { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingBottom: 8 },
  label:    { fontWeight: 700, fontSize: 14 },
  loading:  { fontSize: 12, color: '#8b949e' },
  list:     { flex: 1, overflowY: 'auto' },
  empty:    { color: '#8b949e', fontSize: 12, textAlign: 'center', padding: 16 },
  row:      { display: 'block', textDecoration: 'none', borderBottom: '1px solid #21262d', padding: '7px 2px' },
  title:    { color: '#c9d1d9', fontSize: 13, lineHeight: 1.5 },
  meta:     { fontSize: 11, color: '#8b949e', marginTop: 3 },
  sentinel: { textAlign: 'center', fontSize: 11, color: '#8b949e', padding: '10px 0' },
};
